import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { acars, pilot, aircraft } from "@/lib/api";
import { PlaneTakeoff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OPERATORS } from "@/lib/branding";
import { substitutesFor, pickRegistration, registrationSubstituted } from "@/lib/aircraft";
import { Link } from "react-router-dom";
import RefreshBidsButton from "@/components/RefreshBidsButton";
import { BID_QUERY_OPTIONS } from "@/lib/bids";

/**
 * Init Flight
 * -----------
 * Pilots MUST pick from an existing bid – no manual entry. They then pick a
 * specific aircraft registration from the fleet published on flyvac.org.
 * Block fuel and gates are NOT captured here — fuel comes from the SimBrief
 * OFP loaded on the Flight Plan page, gates from FlightAware / Aviation Stack / manual
 * override / dispatch.
 */
export default function FlightInit() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data: active } = useQuery({ queryKey: ["acars-active"], queryFn: acars.active });
  const { data: bids = [], isLoading: bidsLoading } = useQuery({ queryKey: ["my-bids"], queryFn: pilot.bids, ...BID_QUERY_OPTIONS });

  const initialBidId = sp.get("bid") || "";
  const [bidId, setBidId]   = useState(initialBidId);
  const [type, setType]     = useState(sp.get("type") || "");
  // What the pilot explicitly picked. Empty means "whatever the assignment
  // says" — the selection itself is derived below, not stored, so an in-flight
  // fleet query can no longer blank it.
  const [regChoice, setRegChoice] = useState("");

  // Selected bid object
  const bid = bids.find((b) => String(b.id) === String(bidId));
  const scheduledType = bid?.type || sp.get("type") || "";
  const subs = substitutesFor(scheduledType);

  // Default aircraft to the scheduled type when the bid changes.
  //
  // This used to strip a leading B/A before a digit — B38M -> 38M — borrowing
  // the normalisation aircraftGroup() uses to match the IATA-ish codes in
  // AIRCRAFT_GROUPS. That normalisation is correct for group matching and wrong
  // everywhere else: the fleet catalogue is keyed on ICAO, so "38M" matched
  // nothing ("No registrations found for 38M" while the field displayed B38M),
  // and line ~80 submitted the same stripped value as the flight's aircraft
  // type — so the live row and the filed PIREP recorded a code that does not
  // exist in the fleet.
  //
  // substitutesFor() below does its own normalisation internally, so nothing
  // here needs a stripped copy.
  useEffect(() => {
    if (bid) {
      setType(String(bid.type || "").toUpperCase());
      // Drop the previous flight's override; the new bid's own tail takes over.
      setRegChoice("");
    }
  }, [bid?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first bid if none provided in URL
  useEffect(() => {
    if (!bidId && bids.length === 1) setBidId(String(bids[0].id));
  }, [bids, bidId]);

  // Operator derived from bid.opr, else default from URL or "AC"
  const operator = (bid?.opr || sp.get("opr") || "AC").toUpperCase();

  // Registrations for the currently selected type (from VAC catalogue).
  const regQueryIcao = (type || bid?.type || "").toUpperCase();
  const { data: regData, isLoading: regsLoading } = useQuery({
    queryKey: ["ac-regs", regQueryIcao],
    queryFn: () => aircraft.registrations(regQueryIcao),
    enabled: !!regQueryIcao,
    staleTime: 3600_000,
  });
  const regs = regData?.registrations || [];

  // The aircraft the pilot was actually assigned when they bid, and the
  // selection that follows from it. See pickRegistration() for why the
  // assignment wins over the catalogue's first entry.
  const assignedReg = String(bid?.reg || "").toUpperCase();
  const reg = pickRegistration(regs, assignedReg, regChoice);
  const regSubstituted = registrationSubstituted(regs, assignedReg);

  const init = useMutation({
    mutationFn: () => {
      if (!bid) throw new Error("Pick a bid first");
      return acars.init({
        flt: String(bid.flt || bid.fltnum || ""),
        orig: bid.orig,
        dest: bid.dest,
        type: (type || bid.type || "").toUpperCase(),
        reg: reg || null,
        assignment_id: bid.id,
        operator,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries();
      // Not "Start MSFS": X-Plane is supported now, and on macOS and Linux it
      // is the ONLY thing the desktop app will look for.
      toast.success("ACARS session initialised. Start your simulator to begin telemetry.");
      nav("/tracking");
    },
    onError: (e) => toast.error(e.response?.data?.detail || e.message || "Init failed"),
  });

  // Registration is required — without it the filed PIREP has no tail number,
  // which VACARS3 dispatch panel + hours reporting can't reconcile.
  const canInit = bid && (type || bid.type) && reg && !active;

  return (
    <div className="p-8 max-w-4xl" data-testid="init-page">
      <div className="mb-6">
        <div className="overline mb-1">ACARS</div>
        <h1 className="text-3xl font-bold tracking-tight">Initialise flight</h1>
        <div className="acbar" />
        <p className="text-sm text-muted-foreground mt-4">
          Pick one of your bidded flights and file it with the crew system.
          Aircraft type and registration come from your assignment — the type is
          locked and can only be changed on myvac.flyvac.org; change the tail only if
          you are not flying the one you were given. Fuel is pulled from your
          SimBrief OFP.
        </p>
      </div>

      {active && (
        <div className="mb-6 card-flat px-5 py-4 border-primary bg-primary/10" data-testid="init-blocker">
          <div className="overline mb-1 text-primary">Blocked</div>
          <div className="text-sm">
            You already have an active flight (
            <span className="font-mono">{active.flt} · {active.orig}→{active.dest}</span>
            ). Complete or cancel it from the tracking view first.
          </div>
        </div>
      )}

      {(!bidsLoading && bids.length === 0) ? (
        <div className="card-flat p-8 text-center" data-testid="init-no-bids">
          <div className="text-lg font-bold mb-2">No bids yet</div>
          <p className="text-sm text-muted-foreground mb-4">
            You need to bid on a flight before you can initialise it. Bids are
            placed on the FlyVAC website, and show up here when you come back
            to VACARS5.
          </p>
          <div className="flex items-center justify-center gap-3">
          <RefreshBidsButton />
          <a
            href="https://myvac.flyvac.org/bids.php"
            target="_blank"
            rel="noreferrer"
            data-testid="init-goto-website"
            className="inline-block bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest px-5 py-2.5 rounded-md hover:brightness-110"
          >
            Bid on myvac.flyvac.org →
          </a>
          </div>
        </div>
      ) : (
        <div className="card-flat p-6" data-testid="init-form">
          {/* A div, not a label: the Refresh button would be the label's
              first control, so clicking "Select bid" would press it. */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="init-select-bid" className="overline">Select bid</label>
              <RefreshBidsButton />
            </div>
            <select
              id="init-select-bid"
              value={bidId}
              onChange={(e) => setBidId(e.target.value)}
              data-testid="init-select-bid"
              className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-primary"
            >
              <option value="">— Choose a bidded flight —</option>
              {bids.map((b) => (
                <option key={b.id} value={b.id}>
                  {`${b.flt || b.fltnum} · ${b.orig} → ${b.dest} · ${b.type}`}
                </option>
              ))}
            </select>
          </div>

          {bid && (
            <>
              {/* Summary of the selected flight */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-border rounded-md overflow-hidden mb-6" data-testid="init-summary">
                <Summary label="Flight #"  value={`${bid.opr || "AC"}${bid.flt || bid.fltnum}`} />
                <Summary label="Operator"  value={(OPERATORS.find(o => o.code === operator)?.name) || operator} />
                <Summary label="Route"     value={`${bid.orig} → ${bid.dest}`} />
                <Summary label="Assigned"  value={assignedReg} last />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                  <span className="overline block mb-1.5">Aircraft type</span>
                  <div
                    data-testid="init-type"
                    className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono text-foreground"
                  >
                    {bid.type}
                  </div>
                  <span className="text-[11px] text-muted-foreground mt-1 block">
                    Locked to your bid — change type on myvac.flyvac.org before flying.
                  </span>
                </label>

                <label className="block">
                  <span className="overline block mb-1.5">
                    Aircraft registration
                    {regsLoading && <Loader2 className="inline w-3 h-3 animate-spin ml-2" />}
                  </span>
                  {regs.length > 0 ? (
                    <select
                      value={reg}
                      onChange={(e) => setRegChoice(e.target.value)}
                      data-testid="init-reg"
                      className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-primary"
                    >
                      {regs.map((r) => (
                        <option key={r.reg} value={r.reg}>
                          {r.reg}{r.num ? ` · ${r.num}` : ""}{r.type_short ? ` · ${r.type_short}` : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono text-muted-foreground" data-testid="init-reg-empty">
                      {regsLoading ? "Loading fleet…" : `No registrations found for ${regQueryIcao || "this type"}.`}
                    </div>
                  )}
                  {regSubstituted && (
                    <span
                      data-testid="init-reg-substituted"
                      className="text-[11px] text-amber-400 mt-1 block"
                    >
                      Crew scheduling assigned <span className="font-mono">{assignedReg}</span>,
                      which is not in the current fleet for {regQueryIcao}. Pick the
                      tail you are actually flying — it is the one filed on your PIREP.
                    </span>
                  )}
                  <span className="text-[11px] text-muted-foreground mt-1 block">
                    Required — this is the tail number filed on your PIREP.
                    Fleet loaded from{" "}
                    <a href="https://myvac.flyvac.org/paperwork/aircraft.txt" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                      myvac.flyvac.org/paperwork/aircraft.txt
                    </a>
                  </span>
                </label>
              </div>

              <div className="mt-8 flex items-center gap-4">
                <button
                  onClick={() => init.mutate()}
                  disabled={!canInit || init.isPending}
                  data-testid="init-submit"
                  className="bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest px-6 py-3 rounded-md hover:brightness-110 active:scale-[0.98] transition-all disabled:opacity-60 flex items-center gap-2"
                >
                  {init.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlaneTakeoff className="w-4 h-4" />}
                  Initialize ACARS
                </button>
                <button
                  onClick={() => nav(-1)}
                  data-testid="init-cancel"
                  className="text-sm text-muted-foreground hover:text-foreground px-4 py-2.5"
                >
                  Cancel
                </button>
                <Link
                  to="/fpls"
                  data-testid="init-goto-fpls"
                  className="text-sm text-muted-foreground hover:text-primary px-4 py-2.5 ml-auto"
                >
                  ← back to Flight Plan
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Summary({ label, value, last }) {
  return (
    <div className={`px-4 py-3 bg-card ${last ? "" : "border-r border-border"}`}>
      <div className="overline mb-1">{label}</div>
      <div className="text-sm font-mono">{value || "—"}</div>
    </div>
  );
}

